import { useState, useCallback, useRef, useEffect } from 'react';
import {
  playCompleteSound,
  playLineSound,
  playWrongSound,
  unlockAudio,
} from '@/pages/tmt-spick/utils/sound';
import {
  STAGE1_CONFIG,
  STAGE2_CONFIG,
  STAGE3_CONFIG,
  TMT_ERROR_PENALTY,
  TMT_COMPLETE_DELAY_MS,
  TMT_TRANSITION_DELAY_MS,
  TMT_NOTICE_MS,
  TMT_TOTAL_DURATION_SEC,
  calculateStageScore,
  calculateFinalScore,
  classifyWrong,
  type TmtStageConfig,
  type TmtNodeSelection,
  type TmtWrongRecord,
  type TmtStageData,
  type TmtGameData,
  type CompletionStatus,
} from '@/pages/tmt-spick/tmtConfig';

export type TmtScreen =
  | 'guide'
  | 'stage1'
  | 'stage2'
  | 'stage3'
  | 'result';

type StageKey = 'stage1' | 'stage2' | 'stage3';

const CONFIG_BY_KEY: Record<StageKey, TmtStageConfig> = {
  stage1: STAGE1_CONFIG,
  stage2: STAGE2_CONFIG,
  stage3: STAGE3_CONFIG,
};

const TOTAL_TIME_MS = TMT_TOTAL_DURATION_SEC * 1000;

// TMT(순서 잇기) 스픽 버전 게임 상태 관리
// 소개 → (1~3단계를 하나로 이어서) → 결과 순서로 진행한다.
// - 단계 구분 표시 없이 총 45초 하나의 타이머로 계속 이어진다.
// - 한 단계의 순서를 모두 맞추면 아주 짧은 완성 피드백 후 바로 다음 단계로 넘어간다.
// - 시간이 끝나면 진행 중이던 단계까지 기록하고 결과 화면으로 이동한다.
// - 오답이 기준 횟수에 도달하면 한 번 더 기회를 주고, 그마저 넘기면 다음 단계로 넘어간다.
export function useGameState() {
  const [screen, setScreen] = useState<TmtScreen>('guide');
  const [showExitModal, setShowExitModal] = useState(false);

  const [path, setPath] = useState<string[]>([]);
  const [wrongId, setWrongId] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [transitionMsg, setTransitionMsg] = useState<string | null>(null);
  const [remainingMs, setRemainingMs] = useState(TOTAL_TIME_MS);

  const [score, setScore] = useState(0);
  const [totalTimeMs, setTotalTimeMs] = useState(0);
  const [totalWrongCount, setTotalWrongCount] = useState(0);

  // 현재 Stage 런타임
  const stageKeyRef = useRef<StageKey>('stage1');
  const configRef = useRef<TmtStageConfig>(STAGE1_CONFIG);
  const pathRef = useRef<string[]>([]);
  const isActiveRef = useRef(false);
  const stageEndedRef = useRef(false);
  const gameEndedRef = useRef(false);
  const extraChanceUsedRef = useRef(false);
  const wrongCountRef = useRef(0);
  const selectionsRef = useRef<TmtNodeSelection[]>([]);
  const wrongSelectionsRef = useRef<string[]>([]);
  const wrongDetailRef = useRef<TmtWrongRecord[]>([]);
  const lastCorrectNodeRef = useRef<string | null>(null);
  const maxErrorsRef = useRef(0);
  const startTimeRef = useRef(0);
  const lastSelectTimeRef = useRef(0);
  const gameEndTimeRef = useRef(0);
  const onTimeUpRef = useRef<() => void>(() => {});

  // Stage 결과 보관
  const stage1ResultRef = useRef<TmtStageData | null>(null);
  const stage2ResultRef = useRef<TmtStageData | null>(null);
  const stage3ResultRef = useRef<TmtStageData | null>(null);

  // 다음 Stage 전환 함수 (순환 참조 회피용 ref)
  const advanceRef = useRef<() => void>(() => {});

  const timerRef = useRef<number | null>(null);
  const wrongTimerRef = useRef<number | null>(null);
  const completeTimerRef = useRef<number | null>(null);
  const transitionTimerRef = useRef<number | null>(null);
  const noticeTimerRef = useRef<number | null>(null);
  const errorLimitTimerRef = useRef<number | null>(null);

  const clearTimers = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (wrongTimerRef.current !== null) {
      clearTimeout(wrongTimerRef.current);
      wrongTimerRef.current = null;
    }
    if (completeTimerRef.current !== null) {
      clearTimeout(completeTimerRef.current);
      completeTimerRef.current = null;
    }
    if (transitionTimerRef.current !== null) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
    if (noticeTimerRef.current !== null) {
      clearTimeout(noticeTimerRef.current);
      noticeTimerRef.current = null;
    }
    if (errorLimitTimerRef.current !== null) {
      clearTimeout(errorLimitTimerRef.current);
      errorLimitTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const stopTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // 현재 Stage 종료 데이터 생성
  const buildStageData = useCallback((status: CompletionStatus): TmtStageData => {
    const cfg = configRef.current;
    const timeMs = Date.now() - startTimeRef.current;
    const wrongCount = wrongCountRef.current;
    const adjustedTimeSec = timeMs / 1000 + wrongCount * TMT_ERROR_PENALTY;
    const completedStage = status === 'completed';
    const stageScore = calculateStageScore(
      cfg.maxScore,
      cfg.fastTime,
      cfg.slowTime,
      adjustedTimeSec,
      pathRef.current.length,
      cfg.order.length,
      completedStage,
    );
    return {
      stage: stageKeyRef.current as 'stage1' | 'stage2' | 'stage3',
      completionStatus: status,
      timeMs,
      adjustedTimeSec: Math.round(adjustedTimeSec * 10) / 10,
      wrongCount,
      completedCorrectCount: pathRef.current.length,
      totalCorrectCount: cfg.order.length,
      selections: selectionsRef.current,
      wrongSelections: wrongSelectionsRef.current,
      wrongDetail: wrongDetailRef.current,
      lastCorrectNode: lastCorrectNodeRef.current,
      endedAt: new Date().toISOString(),
      stageScore: Math.round(stageScore * 10) / 10,
    };
  }, []);

  // 현재 Stage 결과를 해당 슬롯에 보관
  const recordCurrentStage = useCallback(
    (status: CompletionStatus) => {
      const data = buildStageData(status);
      const key = stageKeyRef.current;
      if (key === 'stage1') stage1ResultRef.current = data;
      else if (key === 'stage2') stage2ResultRef.current = data;
      else stage3ResultRef.current = data;
    },
    [buildStageData],
  );

  // 전체 결과 계산 후 결과 화면 이동
  const finishGame = useCallback(() => {
    if (gameEndedRef.current) return;
    gameEndedRef.current = true;
    isActiveRef.current = false;
    stageEndedRef.current = true;
    stopTimer();

    const s1 = stage1ResultRef.current;
    const s2 = stage2ResultRef.current;
    const s3 = stage3ResultRef.current;

    const totalTime = (s1?.timeMs ?? 0) + (s2?.timeMs ?? 0) + (s3?.timeMs ?? 0);
    const totalWrong = (s1?.wrongCount ?? 0) + (s2?.wrongCount ?? 0) + (s3?.wrongCount ?? 0);
    const completedStageCount = [s1, s2, s3].filter(
      (s) => s?.completionStatus === 'completed',
    ).length;
    const finalScore = calculateFinalScore([
      s1?.stageScore ?? 0,
      s2?.stageScore ?? 0,
      s3?.stageScore ?? 0,
    ]);

    const gameData: TmtGameData = {
      gameType: 'tmt',
      performedAt: new Date().toISOString(),
      stage1: s1,
      stage2: s2,
      stage3: s3,
      totalTimeMs: totalTime,
      totalWrongCount: totalWrong,
      completedStageCount,
      stageScores: {
        stage1: s1?.stageScore ?? 0,
        stage2: s2?.stageScore ?? 0,
        stage3: s3?.stageScore ?? 0,
      },
      score: finalScore,
      isAborted: false,
    };

    setScore(finalScore);
    setTotalTimeMs(totalTime);
    setTotalWrongCount(totalWrong);
    setCompleted(false);
    setTransitionMsg(null);
    setRemainingMs(0);
    console.log('TMT 스픽 본게임 데이터:', gameData);
    setScreen('result');
  }, [stopTimer]);

  // 총 45초 종료: 진행 중이던 단계까지 기록하고 결과 화면으로 이동
  const handleTimeUp = useCallback(() => {
    if (gameEndedRef.current) return;
    if (!stageEndedRef.current) {
      stageEndedRef.current = true;
      isActiveRef.current = false;
      recordCurrentStage('time_limit');
    }
    finishGame();
  }, [finishGame, recordCurrentStage]);

  useEffect(() => {
    onTimeUpRef.current = handleTimeUp;
  }, [handleTimeUp]);

  // 오답 횟수 초과로 단계 종료: 다음 단계로 넘어가거나(마지막 단계면) 결과로 이동
  const handleStageFail = useCallback(() => {
    if (stageEndedRef.current || gameEndedRef.current) return;
    stageEndedRef.current = true;
    isActiveRef.current = false;
    recordCurrentStage('error_limit');

    const key = stageKeyRef.current;
    if (key === 'stage3') {
      transitionTimerRef.current = window.setTimeout(
        () => finishGame(),
        TMT_TRANSITION_DELAY_MS,
      );
      return;
    }
    setTransitionMsg('이어서 계속할게요.');
    transitionTimerRef.current = window.setTimeout(() => {
      setTransitionMsg(null);
      advanceRef.current();
    }, TMT_TRANSITION_DELAY_MS);
  }, [recordCurrentStage, finishGame]);

  // 정상 완료: 아주 짧은 완성 피드백 후 바로 다음 단계로 전환
  const handleStageComplete = useCallback(() => {
    if (stageEndedRef.current || gameEndedRef.current) return;
    stageEndedRef.current = true;
    isActiveRef.current = false;
    setCompleted(true);
    recordCurrentStage('completed');

    completeTimerRef.current = window.setTimeout(() => {
      if (gameEndedRef.current) return;
      advanceRef.current();
    }, TMT_COMPLETE_DELAY_MS);
  }, [recordCurrentStage]);

  // Stage 시작: 단계별 런타임만 초기화한다. (타이머는 게임 시작 시 하나만 동작)
  const beginStage = useCallback((key: StageKey) => {
    const cfg = CONFIG_BY_KEY[key];
    stageKeyRef.current = key;
    configRef.current = cfg;
    maxErrorsRef.current = cfg.maxErrors;
    pathRef.current = [];
    wrongCountRef.current = 0;
    extraChanceUsedRef.current = false;
    selectionsRef.current = [];
    wrongSelectionsRef.current = [];
    wrongDetailRef.current = [];
    lastCorrectNodeRef.current = null;
    stageEndedRef.current = false;
    isActiveRef.current = true;
    startTimeRef.current = Date.now();
    lastSelectTimeRef.current = startTimeRef.current;

    if (completeTimerRef.current !== null) {
      clearTimeout(completeTimerRef.current);
      completeTimerRef.current = null;
    }
    if (transitionTimerRef.current !== null) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
    if (noticeTimerRef.current !== null) {
      clearTimeout(noticeTimerRef.current);
      noticeTimerRef.current = null;
    }
    if (errorLimitTimerRef.current !== null) {
      clearTimeout(errorLimitTimerRef.current);
      errorLimitTimerRef.current = null;
    }

    setPath([]);
    setWrongId(null);
    setCompleted(false);
    setTransitionMsg(null);
    setScreen(key as TmtScreen);
  }, []);

  // 다음 Stage 전환
  const advance = useCallback(() => {
    const key = stageKeyRef.current;
    if (key === 'stage3') {
      finishGame();
      return;
    }
    const next: Record<StageKey, StageKey> = {
      stage1: 'stage2',
      stage2: 'stage3',
      stage3: 'stage3',
    };
    beginStage(next[key]);
  }, [beginStage, finishGame]);

  useEffect(() => {
    advanceRef.current = advance;
  }, [advance]);

  // 소개 → 게임 시작: 총 45초 타이머 하나를 켜고 1단계부터 이어서 진행
  const startGame = useCallback(() => {
    unlockAudio();
    clearTimers();
    stage1ResultRef.current = null;
    stage2ResultRef.current = null;
    stage3ResultRef.current = null;
    gameEndedRef.current = false;
    gameEndTimeRef.current = Date.now() + TOTAL_TIME_MS;
    setRemainingMs(TOTAL_TIME_MS);

    beginStage('stage1');

    timerRef.current = window.setInterval(() => {
      const remaining = gameEndTimeRef.current - Date.now();
      if (remaining <= 0) {
        setRemainingMs(0);
        onTimeUpRef.current();
      } else {
        setRemainingMs(remaining);
      }
    }, 100);
  }, [clearTimers, beginStage]);

  // 노드 선택 판정 (드래그 종료 노드 기준)
  const handleSelect = useCallback(
    (nodeId: string) => {
      if (!isActiveRef.current || stageEndedRef.current || gameEndedRef.current) return;
      const cfg = configRef.current;
      const idx = pathRef.current.length;
      if (idx >= cfg.order.length) return;

      const expected = cfg.order[idx];
      const now = Date.now();
      const moveTime = idx === 0 ? 0 : now - lastSelectTimeRef.current;
      lastSelectTimeRef.current = now;

      if (nodeId === expected) {
        // 정답을 맞추면 안내 문구는 바로 정리한다
        if (noticeTimerRef.current !== null) {
          clearTimeout(noticeTimerRef.current);
          noticeTimerRef.current = null;
        }
        setTransitionMsg(null);

        selectionsRef.current = [
          ...selectionsRef.current,
          {
            nodeId,
            isCorrect: true,
            selectedAtMs: now - startTimeRef.current,
            moveTimeMs: moveTime,
          },
        ];
        const newPath = [...pathRef.current, nodeId];
        pathRef.current = newPath;
        lastCorrectNodeRef.current = nodeId;
        setPath(newPath);

        if (newPath.length >= cfg.order.length) {
          playCompleteSound();
          handleStageComplete();
        } else {
          // 선이 이어지는 순간의 짧은 소리
          playLineSound();
        }
      } else {
        playWrongSound();
        wrongCountRef.current += 1;
        wrongSelectionsRef.current = [...wrongSelectionsRef.current, nodeId];
        selectionsRef.current = [
          ...selectionsRef.current,
          {
            nodeId,
            isCorrect: false,
            selectedAtMs: now - startTimeRef.current,
            moveTimeMs: moveTime,
          },
        ];
        if (stageKeyRef.current === 'stage2' || stageKeyRef.current === 'stage3') {
          wrongDetailRef.current = [
            ...wrongDetailRef.current,
            classifyWrong(nodeId, cfg.ruleColor, cfg.ruleShape, cfg.nodes),
          ];
        }

        setWrongId(nodeId);
        if (wrongTimerRef.current !== null) clearTimeout(wrongTimerRef.current);
        wrongTimerRef.current = window.setTimeout(() => setWrongId(null), 350);

        // 기준 횟수 도달: Spatial Span처럼 한 번 더 기회를 준다.
        // 그 기회까지 놓치면 다음 단계로 넘어간다.
        if (wrongCountRef.current >= maxErrorsRef.current) {
          if (!extraChanceUsedRef.current) {
            extraChanceUsedRef.current = true;
            setTransitionMsg('한 번 더 집중해볼까요?');
            if (noticeTimerRef.current !== null) clearTimeout(noticeTimerRef.current);
            noticeTimerRef.current = window.setTimeout(
              () => setTransitionMsg(null),
              TMT_NOTICE_MS,
            );
          } else {
            isActiveRef.current = false;
            if (errorLimitTimerRef.current !== null) {
              clearTimeout(errorLimitTimerRef.current);
            }
            errorLimitTimerRef.current = window.setTimeout(() => {
              handleStageFail();
            }, 600);
          }
        }
      }
    },
    [handleStageComplete, handleStageFail],
  );

  const openExitModal = useCallback(() => setShowExitModal(true), []);
  const closeExitModal = useCallback(() => setShowExitModal(false), []);

  // 소개 화면으로 복귀 (진행 중 타이머/연출 정리)
  const goToGuide = useCallback(() => {
    clearTimers();
    isActiveRef.current = false;
    stageEndedRef.current = true;
    gameEndedRef.current = true;
    setShowExitModal(false);
    setPath([]);
    setWrongId(null);
    setCompleted(false);
    setTransitionMsg(null);
    setRemainingMs(TOTAL_TIME_MS);
    setScreen('guide');
  }, [clearTimers]);

  const confirmExit = goToGuide;

  const goToNext = useCallback(() => {
    // TODO: 다음 단계(음성 인지건강 체크)로 이동
  }, []);

  return {
    screen,
    showExitModal,
    path,
    wrongId,
    completed,
    transitionMsg,
    remainingMs,
    score,
    totalTimeMs,
    totalWrongCount,
    startGame,
    handleSelect,
    openExitModal,
    closeExitModal,
    confirmExit,
    goToGuide,
    goToNext,
  };
}